/* eslint-disable react/require-default-props */
import React, { useCallback, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { HarmonizerMessageRoles } from '@medflex/shared-types';
import useClassNames from '@medflex/hooks/useClassNames';
import InquiryCallDetailsMessage, {
  PLACEMENT,
  KINDS as MessageKinds,
} from './InquiryCallDetailsMessage';

const InquiryCallDetailsMessages = ({ original, translated, onAudioSkipSeconds }) => {
  const { t } = useTranslation('components', {
    keyPrefix: 'PamInquiry.InquiryCallDetails.messages',
  });

  const [selectedTab, setSelectedTab] = useState(translated ? 'translated' : 'original');

  const renderMessages = useCallback(
    messages => (
      <div className="flex flex-col gap-y-4">
        {messages.map(message => {
          const { role } = message;

          if (role === HarmonizerMessageRoles.AGENT) {
            return (
              <InquiryCallDetailsMessage
                startElement={<SparklesIcon className="w-6 h-6 flex-shrink-0" />}
                message={message}
                onAudioSkipSeconds={onAudioSkipSeconds}
              />
            );
          }

          return (
            <InquiryCallDetailsMessage
              placement={PLACEMENT.END}
              kind={MessageKinds.SECONDARY}
              message={message}
              onAudioSkipSeconds={onAudioSkipSeconds}
            />
          );
        })}
      </div>
    ),
    [onAudioSkipSeconds],
  );

  const tabs = useMemo(
    () => ({
      original: {
        label: t('tab_original'),
        render: () => renderMessages(original),
      },
      ...(translated && {
        translated: {
          label: t('tab_translated'),
          render: () => renderMessages(translated),
        },
      }),
    }),
    [t, translated, renderMessages, original],
  );
  const tabKeys = useMemo(() => Object.keys(tabs).reverse(), [tabs]);

  const pillBaseClass = 'whitespace-no-wrap text-sm py-1 px-2.5 rounded-full';
  const pillActiveClass = useClassNames(
    () => [pillBaseClass, 'bg-white border-1 border-slate-300'],
    [pillBaseClass],
  );

  return (
    <div className="flex flex-col">
      {tabKeys.length > 1 && (
        <div className="flex self-end bg-slate-200 rounded-full p-1">
          {tabKeys.map(key => {
            if (key === selectedTab || tabKeys.length === 1) {
              return (
                <div type="button" key={key} className={pillActiveClass}>
                  {tabs[key].label}
                </div>
              );
            }

            return (
              <button
                type="button"
                key={key}
                className={pillBaseClass}
                onClick={setSelectedTab.bind(null, key)}
              >
                {tabs[key].label}
              </button>
            );
          })}
        </div>
      )}
      {tabs[selectedTab].render()}
    </div>
  );
};

InquiryCallDetailsMessages.propTypes = {
  original: PropTypes.arrayOf(
    PropTypes.shape({
      role: PropTypes.oneOf(Object.values(HarmonizerMessageRoles)),
      content: PropTypes.string,
      createdAt: PropTypes.string,
      timeInCallSeconds: PropTypes.number,
    }),
  ),
  translated: PropTypes.arrayOf(
    PropTypes.shape({
      role: PropTypes.oneOf(Object.values(HarmonizerMessageRoles)),
      content: PropTypes.string,
      createdAt: PropTypes.string,
      timeInCallSeconds: PropTypes.number,
    }),
  ),
  onAudioSkipSeconds: PropTypes.func.isRequired,
};

export default InquiryCallDetailsMessages;
